import type { ItemMatchedOnModel } from '@supply-mover-2-0/shared';

/** The translation function the label is worded through. */
export type ItemMatchedOnTranslateModel = (key: string, options?: Record<string, string | number | null | undefined>) => string;

export interface ItemMatchedOnLabelModel {
	/** The reason, as the tooltip line reads it. */
	text: string;
	/** More about it for a tooltip (the number), or null. */
	detail: string | null;
}

/**
 * PD-17503 — the words for one reason a search result matched, as the info
 * icon beside its name lists them.
 * Staff screens name whose number it is ("Limbach's item number"); the
 * storefront says "Your item number" and calls an outside number by the
 * number alone, never by whose it is.
 */
export function itemMatchedOnLabel(
	label: ItemMatchedOnModel,
	t: ItemMatchedOnTranslateModel,
	audience: 'staff' | 'storefront' = 'staff',
): ItemMatchedOnLabelModel {
	const number = label.number != null && label.number !== '' ? label.number : null;
	switch (label.kind) {
		case 'client number':
			if (audience === 'storefront') {
				return { text: t('Your item number'), detail: number };
			}
			return {
				text:
					label.clientName != null && label.clientName.trim() !== ''
						? t("{{name}}'s item number", { name: label.clientName })
						: t('Customer item number'),
				detail: number,
			};
		case 'competitor number':
		case 'maker number':
		case 'standard number':
			if (audience === 'storefront') {
				return number != null ? { text: t('Matches {{number}}', { number }), detail: null } : { text: t('Part number'), detail: null };
			}
			return {
				text: label.kind === 'competitor number' ? t('Competitor number') : label.kind === 'maker number' ? t('Maker number') : t('Standard'),
				detail: number,
			};
		case 'word':
		case 'size':
			return { text: t('{{searched}} = {{catalog}}', { searched: label.searched ?? '', catalog: label.catalog ?? '' }), detail: null };
		case 'extended description':
			return { text: t('Extended description'), detail: null };
		case 'specifications':
			return { text: t('Specifications'), detail: null };
	}
}

/** The keys item lists carry their search box under (the Items list, the client and quote shop lists). */
const ITEM_SEARCH_FILTER_KEYS: ReadonlyArray<string> = ['itemSearch', 'item_search', 'search'];

function textOf(value: unknown): string {
	if (typeof value === 'string') {
		return value.trim();
	}
	if (Array.isArray(value)) {
		return value.map(textOf).filter((part) => part !== '').join(' ');
	}
	if (value != null && typeof value === 'object' && 'value' in value) {
		return textOf((value as { value: unknown }).value);
	}
	return '';
}

/**
 * PD-17503 — the text an item list was searched by, from its query
 * parameters: the plain `search`, or the item search filter under whichever
 * key the list uses. Null when the list was not searched.
 */
export function itemListSearchText(search: unknown, filters: unknown): string | null {
	const direct = textOf(search);
	if (direct !== '') {
		return direct;
	}
	if (filters != null && typeof filters === 'object') {
		for (const key of ITEM_SEARCH_FILTER_KEYS) {
			const text = textOf((filters as Record<string, unknown>)[key]);
			if (text !== '') {
				return text;
			}
		}
	}
	return null;
}
